import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { GraphqlFilterRoot } from "../../crudGraphql/domain/models/graphqlModel";
import { useLocation } from "react-router-dom";

interface CrudFilterContextType {
  filters: Record<string, GraphqlFilterRoot>;
  setFilter: (filter: GraphqlFilterRoot, identifier: string) => void;
  getFilter: (
    filter: GraphqlFilterRoot,
    identifier: string,
  ) => GraphqlFilterRoot;
  initFilter: (filter: GraphqlFilterRoot, identifier: string) => void;
}

const defaultContext: CrudFilterContextType = {
  filters: {},
  setFilter: () => {
    return;
  },
  getFilter: () => {
    return {} as GraphqlFilterRoot;
  },
  initFilter: () => {
    return;
  },
};

export const CrudFilterContext =
  createContext<CrudFilterContextType>(defaultContext);

interface CrudFilterProviderProps {
  children: ReactNode;
}

export const CrudFilterProvider: React.FC<CrudFilterProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const [filters, setFilters] = React.useState<
    Record<string, GraphqlFilterRoot>
  >({});
  const [prevLocation, setPrevLocation] = React.useState<string>("");

  const setFilter = useCallback(
    (filter: GraphqlFilterRoot, identifier: string) => {
      if (filter.searchBy === undefined) {
        throw new Error("searchBy is required");
      }

      setFilters((prevFilters) => {
        return { ...prevFilters, [identifier]: filter };
      });
    },
    [],
  );

  const getFilter = useCallback(
    (filter: GraphqlFilterRoot, identifier: string) => {
      if (filter.searchBy === undefined) {
        throw new Error("searchBy is required");
      }

      if (filters[identifier] === undefined) {
        return filter;
      }

      return filters[identifier];
    },
    [filters],
  );

  const initFilter = (filter: GraphqlFilterRoot, identifier: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [identifier]: filter,
    }));
  };

  const value = React.useMemo(
    () => ({
      filters,
      setFilter,
      getFilter,
      initFilter,
    }),
    [setFilter, getFilter, initFilter],
  );

  useEffect(() => {
    if (prevLocation !== location.pathname) {
      setPrevLocation(location.pathname);
      setFilters({});
    }
  }, [location.pathname, prevLocation]);

  return (
    <CrudFilterContext.Provider value={value}>
      {children}
    </CrudFilterContext.Provider>
  );
};

export const useCrudFilter = (
  filtersModel: GraphqlFilterRoot | (() => GraphqlFilterRoot),
  identifier: string,
) => {
  const context = useContext(CrudFilterContext);
  if (context === undefined) {
    throw new Error("useCrudFilter must be used within a CrudFilterProvider");
  }

  if (context.filters[identifier] === undefined) {
    context.initFilter(
      typeof filtersModel === "function" ? filtersModel() : filtersModel,
      identifier,
    );
  }

  return {
    filters: context.getFilter(
      typeof filtersModel === "function" ? filtersModel() : filtersModel,
      identifier,
    ),
    setFilter: context.setFilter,
  };
};

export default CrudFilterProvider;
